import { db } from '../lib/firebase';
import { ref, get, set, update, child, runTransaction, query, orderByKey, limitToLast, endBefore } from 'firebase/database';
import { themes } from '../utils/themes';
import {
  RawSavedWord,
  SavedWord,
  mapRawSavedWord,
  UserStats,
  ActivityType,
  TrainingSessionItem
} from '../types/vocabulary';
import { updateUserScores } from './leaderboardService';
import { incrementChallengeScore } from './challengeService';
import { words as fallbackWords } from '../components/activities/allWords';
import { logUserActivity } from './activityLogService';
import { sanitizeFirebaseKey } from '../utils/firebaseUtils';

// --- Firebase Helpers ---

export async function fetchSavedWords(userId: string): Promise<SavedWord[]> {
  const snapshot = await get(child(ref(db), `savedWords/${userId}`));
  if (!snapshot.exists()) {
    return [];
  }
  const data = snapshot.val();
  return Object.entries(data).map(([key, value]) =>
    mapRawSavedWord(userId, key, value as RawSavedWord)
  );
}

export async function fetchSavedExpressions(userId: string): Promise<SavedWord[]> {
  const snapshot = await get(child(ref(db), `savedExpressions/${userId}`));
  if (!snapshot.exists()) {
    return [];
  }
  const data = snapshot.val();
  return Object.entries(data).map(([key, value]) =>
    mapRawSavedWord(userId, key, value as RawSavedWord)
  );
}

export async function updateSavedWord(userId: string, wordId: string, partial: Partial<RawSavedWord>): Promise<void> {
  const safeId = sanitizeFirebaseKey(wordId);
  await update(ref(db, `savedWords/${userId}/${safeId}`), partial);
}

export async function updateWordStats(userId: string, wordId: string, { success }: { success: boolean }): Promise<void> {
  const safeId = sanitizeFirebaseKey(wordId);
  let wordRef = ref(db, `savedWords/${userId}/${safeId}`);
  let snapshot = await get(wordRef);

  if (!snapshot.exists()) {
    wordRef = ref(db, `savedExpressions/${userId}/${safeId}`);
    snapshot = await get(wordRef);
    if (!snapshot.exists()) return;
  }

  const word = snapshot.val() as RawSavedWord;
  const updates: Partial<RawSavedWord> = {
    lastPractice: Date.now(),
  };

  if (success) {
    updates.successCount = (word.successCount || 0) + 1;
    // Simple heuristic: increase progress on success, max 100
    updates.progress = Math.min((word.progress || 0) + 5, 100);
  } else {
    updates.failCount = (word.failCount || 0) + 1;
    // Decrease progress on fail, min 0
    updates.progress = Math.max((word.progress || 0) - 5, 0);
  }

  await update(wordRef, updates);

  if (success) {
    const theme = themes.find(t => t.words.includes(wordId));
    if (theme) {
      const themeRef = ref(db, `users/${userId}/themes/${theme.name}`);
      const themeSnapshot = await get(themeRef);
      let themeData = themeSnapshot.val();
      if (!themeData) {
        themeData = {
          theme: theme.name,
          progress: 0,
          createdAt: Date.now(),
          lastPractice: Date.now()
        };
      }
      themeData.progress = (themeData.progress || 0) + 1;
      themeData.lastPractice = Date.now();
      await update(themeRef, themeData);
    }
  }
}

export async function getUserStats(userId: string): Promise<UserStats> {
  const snapshot = await get(child(ref(db), `userStats/${userId}`));
  if (!snapshot.exists()) {
    return {
      userId,
      totalPoints: 0,
      dailyPoints: {},
      streakCount: 0,
      wordDayStreak: 0,
    };
  }
  const stats = snapshot.val() as UserStats;
  
  // Ensure wordDayStreak exists in stats object
  if (stats.wordDayStreak === undefined) {
    stats.wordDayStreak = 0;
  }
  
  // Recalculate streaks based on lastActiveDate and lastWordDayDate to ensure accuracy
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  // Recalculate training streak based on lastActiveDate
  // If lastActiveDate is today, trust the streakCount that was set by updateUserStatsAfterActivity
  // Only recalculate if lastActiveDate is not today (to handle edge cases)
  let needsSave = false;
  if (stats.lastActiveDate) {
    if (stats.lastActiveDate === today) {
      // Trained today - trust the streakCount from updateUserStatsAfterActivity
      // It was already calculated correctly: if yesterday was completed, streak was incremented
      // Only ensure it's at least 1 if somehow it's 0 or undefined (shouldn't happen)
      if (!stats.streakCount || stats.streakCount === 0) {
        stats.streakCount = 1;
        needsSave = true;
      }
      // Don't override the streak - it was already calculated correctly by updateUserStatsAfterActivity
    } else if (stats.lastActiveDate === yesterdayStr) {
      // lastActiveDate is still yesterday - this means updateUserStatsAfterActivity hasn't run yet
      // or there's a race condition. Check if today was completed.
      const todayCompleted = localStorage.getItem(`${COMPLETION_KEY}_${userId}_${today}`) === 'true';
      if (todayCompleted) {
        // User completed both yesterday and today - streak should be yesterday's streak + 1
        // If yesterday was day 1, today should be day 2
        const oldStreak = stats.streakCount || 1;
        stats.streakCount = oldStreak + 1;
        // Also update lastActiveDate to today to prevent future recalculations
        stats.lastActiveDate = today;
        needsSave = true;
      }
      // If today is not completed yet, keep the streak as-is (don't reset)
      // The streak represents consecutive days of training, and the user
      // still has until end of today to continue their streak
    } else {
      // Last active was more than 1 day ago - reset streak to 0
      const lastActiveDate = new Date(stats.lastActiveDate + 'T00:00:00Z');
      const daysDiff = Math.floor((Date.now() - lastActiveDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff > 1 && stats.streakCount !== 0) {
        // More than 1 day ago, reset streak to 0
        stats.streakCount = 0;
        needsSave = true;
      }
    }
  } else {
    // No lastActiveDate - check if today was completed
    const todayCompleted = localStorage.getItem(`${COMPLETION_KEY}_${userId}_${today}`) === 'true';
    if (todayCompleted) {
      stats.streakCount = 1;
      stats.lastActiveDate = today;
      needsSave = true;
    } else if (stats.streakCount !== 0) {
      stats.streakCount = 0;
      needsSave = true;
    }
  }
  
  // Save updated stats if we made changes
  if (needsSave) {
    const statsRef = ref(db, `userStats/${userId}`);
    // Save asynchronously without blocking
    set(statsRef, stats).catch(console.error);
  }

  // Recalculate Word of the Day streak based on lastWordDayDate
  // If user completed Word of the Day yesterday or today, streak should be at least 1
  if (stats.lastWordDayDate) {
    if (stats.lastWordDayDate === today) {
      // Completed Word of the Day today - keep current streak (should be at least 1)
      if (!stats.wordDayStreak || stats.wordDayStreak === 0) {
        stats.wordDayStreak = 1;
        needsSave = true;
      }
    } else if (stats.lastWordDayDate === yesterdayStr) {
      // Completed Word of the Day yesterday but not today yet
      // Preserve the existing streak - don't reset it to 1
      // The streak will be incremented when updateWordDayStreak is called after completing today
      // Only ensure it's at least 1 if it's somehow 0 or undefined
      if (!stats.wordDayStreak || stats.wordDayStreak === 0) {
        stats.wordDayStreak = 1;
        needsSave = true;
      }
      // Otherwise, keep the existing streak value - don't override it
    } else {
      // Last Word of the Day was more than 1 day ago - reset streak to 0
      const lastWordDayDate = new Date(stats.lastWordDayDate + 'T00:00:00Z');
      const daysDiff = Math.floor((Date.now() - lastWordDayDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff > 1) {
        // More than 1 day ago, reset streak to 0
        if (stats.wordDayStreak !== 0) {
          stats.wordDayStreak = 0;
          needsSave = true;
        }
      }
    }
  } else {
    // No lastWordDayDate - reset streak to 0
    if (stats.wordDayStreak !== 0) {
      stats.wordDayStreak = 0;
      needsSave = true;
    }
  }
  
  return stats;
}

export async function updateUserStatsAfterActivity(
  userId: string, 
  { success, date }: { success: boolean, date: string }, 
  user?: any,
  logDetails?: { activityType: string; word?: string }
): Promise<void> {
  const statsRef = ref(db, `userStats/${userId}`);
  
  // Get stats directly from Firebase to avoid getUserStats recalculation issues
  const snapshot = await get(statsRef);
  const stats: UserStats = snapshot.exists() 
    ? snapshot.val() 
    : {
        userId,
        totalPoints: 0,
        dailyPoints: {},
        streakCount: 0,
        wordDayStreak: 0,
      };

  // Update points
  const pointsEarned = success ? 10 : 1; // 10 pts for success, 1 for effort
  stats.totalPoints = (stats.totalPoints || 0) + pointsEarned;

  // Update Leaderboard
  try {
    await updateUserScores(userId, pointsEarned, user || {});
    await incrementChallengeScore(userId, pointsEarned, 'daily_training');
  } catch (error) {
    console.error('Error updating leaderboard scores:', error);
  }

  if (!stats.dailyPoints) stats.dailyPoints = {};
  stats.dailyPoints[date] = (stats.dailyPoints[date] || 0) + pointsEarned;

  // Update Streak logic
  // If lastActiveDate was yesterday, increment streak.
  // If lastActiveDate was today, do nothing to streak count (already handled).
  // If lastActiveDate was before yesterday, reset streak to 1.

  const today = new Date().toISOString().split('T')[0];
  const lastActive = stats.lastActiveDate;

  if (lastActive !== today) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (lastActive === yesterdayStr) {
      // Consecutive day - increment streak
      // Use the current streakCount from Firebase, don't default to 0
      const currentStreak = stats.streakCount || 0;
      stats.streakCount = currentStreak + 1;
    } else if (!lastActive) {
      // First time training - start streak at 1
      stats.streakCount = 1;
    } else {
      // Missed a day or more - reset streak to 1
      stats.streakCount = 1;
    }
    stats.lastActiveDate = today;
  } else {
    // Already trained today - ensure streak is at least 1
    if (!stats.streakCount || stats.streakCount === 0) {
      stats.streakCount = 1;
    }
  }

  await set(statsRef, stats);
  
  // Update the user's lastPractice date so it correctly resets "days without studying"
  try {
    const userRef = ref(db, `users/${userId}`);
    const userSnap = await get(userRef);
    if (userSnap.exists()) {
      await update(userRef, { lastPractice: Date.now() });
    }
  } catch (error) {
    console.error('Error updating user lastPractice:', error);
  }
}

export async function incrementCompletedSessions(userId: string): Promise<void> {
  const statsRef = ref(db, `userStats/${userId}`);

  try {
    await runTransaction(statsRef, (stats) => {
      if (!stats) {
        return { completedSessions: 1 };
      }
      stats.completedSessions = (stats.completedSessions || 0) + 1;
      return stats;
    });
  } catch (error) {
    console.error('Error incrementing completed sessions:', error);
  }
}

export async function updateWordDayStreak(userId: string, totalPointsEarned: number = 100): Promise<void> {
  const statsRef = ref(db, `userStats/${userId}`);
  
  // Get stats directly from Firebase to avoid getUserStats recalculation issues
  const snapshot = await get(statsRef);
  const stats: UserStats = snapshot.exists() 
    ? snapshot.val() 
    : {
        userId,
        totalPoints: 0,
        dailyPoints: {},
        streakCount: 0,
        wordDayStreak: 0,
      };

  const today = new Date().toISOString().split('T')[0];
  const lastActive = stats.lastWordDayDate;

  // If last active is not today, update streak and award points
  if (lastActive !== today) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (lastActive === yesterdayStr) {
      // Consecutive day - increment streak
      // Use the current wordDayStreak from Firebase, don't default to 0
      const currentStreak = stats.wordDayStreak || 0;
      stats.wordDayStreak = currentStreak + 1;
    } else if (!lastActive) {
      // First time completing Word of the Day - start streak at 1
      stats.wordDayStreak = 1;
    } else {
      // Missed a day or more - reset streak to 1
      stats.wordDayStreak = 1;
    }
    stats.lastWordDayDate = today;

    // Increment absolute count for Word of the Day
    stats.wordDayCount = (stats.wordDayCount || 0) + 1;

    // Award the total accumulated points for the session
    stats.totalPoints = (stats.totalPoints || 0) + totalPointsEarned;

    // Update daily points
    if (!stats.dailyPoints) stats.dailyPoints = {};
    stats.dailyPoints[today] = (stats.dailyPoints[today] || 0) + totalPointsEarned;

    // Update leaderboard with the new points
    try {
      await updateUserScores(userId, totalPointsEarned, {});
      await incrementChallengeScore(userId, totalPointsEarned, 'word_of_day');
      
      const userRef = ref(db, `users/${userId}`);
      const userSnap = await get(userRef);
      const userName = userSnap.exists() ? (userSnap.val().displayName || userSnap.val().username || 'User') : 'User';
      await logUserActivity(userId, userName, 'word_of_day', totalPointsEarned, undefined);
    } catch (error) {
      console.error('Error updating leaderboard for Word of the Day:', error);
    }

    await set(statsRef, stats);
  } else {
    // If already completed today, ensure streak is at least 1 (fix for edge cases)
    if (!stats.wordDayStreak || stats.wordDayStreak === 0) {
      stats.wordDayStreak = 1;
      await set(statsRef, stats);
    }
  }

  // Update the user's lastPractice date so it correctly resets "days without studying"
  try {
    const userRef = ref(db, `users/${userId}`);
    const userSnap = await get(userRef);
    if (userSnap.exists()) {
      await update(userRef, { lastPractice: Date.now() });
    }
  } catch (error) {
    console.error('Error updating user lastPractice:', error);
  }
}

// --- Daily Challenge Logic ---

const SELECTION_CACHE_KEY = 'dailyChallengeSelection';
const COMPLETION_KEY = 'dailyChallengeCompleted';
const PROGRESS_KEY = 'dailyTrainingProgress';
const CACHE_VERSION = 2; // Increment when changing daily goal or logic

export function checkDailyTrainingCompleted(userId: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  const key = `${COMPLETION_KEY}_${userId}_${today}`;
  const completed = localStorage.getItem(key) === 'true';
  
  // Clean up old completion keys (from previous days) to prevent confusion
  // This ensures we only check today's completion status
  if (!completed) {
    // If not completed today, check and clean up any old keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (storageKey && storageKey.startsWith(`${COMPLETION_KEY}_${userId}_`)) {
        const dateFromKey = storageKey.split('_').pop();
        if (dateFromKey && dateFromKey !== today) {
          keysToRemove.push(storageKey);
        }
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
  
  return completed;
}

export function markDailyTrainingCompleted(userId: string): void {
  const today = new Date().toISOString().split('T')[0];
  const key = `${COMPLETION_KEY}_${userId}_${today}`;
  localStorage.setItem(key, 'true');
}

export function resetDailyTraining(userId: string): void {
  const today = new Date().toISOString().split('T')[0];
  const completionKey = `${COMPLETION_KEY}_${userId}_${today}`;
  const progressKey = `${PROGRESS_KEY}_${userId}_${today}`;
  localStorage.removeItem(completionKey);
  localStorage.removeItem(progressKey);
  localStorage.removeItem(SELECTION_CACHE_KEY);
}

interface TrainingProgress {
  date: string; // ISO Date string YYYY-MM-DD
  userId: string;
  currentWordIndex: number;
  currentActivityIndex: number;
}

export function saveTrainingProgress(userId: string, currentWordIndex: number, currentActivityIndex: number): void {
  const today = new Date().toISOString().split('T')[0];
  const progressKey = `${PROGRESS_KEY}_${userId}_${today}`;
  const progress: TrainingProgress = {
    date: today,
    userId,
    currentWordIndex,
    currentActivityIndex
  };
  localStorage.setItem(progressKey, JSON.stringify(progress));
}

export function loadTrainingProgress(userId: string): { currentWordIndex: number; currentActivityIndex: number } | null {
  const today = new Date().toISOString().split('T')[0];
  const progressKey = `${PROGRESS_KEY}_${userId}_${today}`;
  const progressStr = localStorage.getItem(progressKey);
  
  if (!progressStr) return null;
  
  try {
    const progress: TrainingProgress = JSON.parse(progressStr);
    // Only return if it's for today and same user
    if (progress.date === today && progress.userId === userId) {
      return {
        currentWordIndex: progress.currentWordIndex,
        currentActivityIndex: progress.currentActivityIndex
      };
    }
  } catch (e) {
    console.error('Error loading training progress:', e);
  }
  
  return null;
}

export function clearTrainingProgress(userId: string): void {
  const today = new Date().toISOString().split('T')[0];
  const progressKey = `${PROGRESS_KEY}_${userId}_${today}`;
  localStorage.removeItem(progressKey);
}

interface CachedSelection {
  date: string; // ISO Date string YYYY-MM-DD
  userId: string;
  version?: number; // Cache version for invalidation
  items: { wordId: string; activities: ActivityType[] }[];
}

export async function getDailyChallenge(userId: string): Promise<TrainingSessionItem[]> {
  const today = new Date().toISOString().split('T')[0];
  const DAILY_GOAL = 15;
  const cacheStr = localStorage.getItem(SELECTION_CACHE_KEY);

  // 1. Check Cache
  if (cacheStr) {
    try {
      const cache: CachedSelection = JSON.parse(cacheStr);
      // Invalidate cache if version doesn't match or date/user doesn't match
      // IMPORTANT: Only use cache if date is TODAY - if it's from yesterday or earlier, invalidate it
      if (cache.date === today && cache.userId === userId && (cache.version || 1) === CACHE_VERSION) {
         // Check that training is NOT completed for today (should allow new training)
         const completionKey = `${COMPLETION_KEY}_${userId}_${today}`;
         const isCompletedToday = localStorage.getItem(completionKey) === 'true';
         
         // Only use cache if training is not completed for today
         if (!isCompletedToday) {
           // Hydrate words
           const allWords = await fetchSavedWords(userId);
           const allExpressions = await fetchSavedExpressions(userId);
           const allItems = [...allWords, ...allExpressions];
           const wordMap = new Map(allItems.map(w => [w.id, w]));

           const sessionItems: TrainingSessionItem[] = [];
           let totalActivities = 0;
           
           for (const item of cache.items) {
             if (totalActivities >= DAILY_GOAL) break;
             
             const word = wordMap.get(item.wordId);
             if (word) {
               // Limit activities for this word to respect daily goal
               const remaining = DAILY_GOAL - totalActivities;
               const activities = item.activities.slice(0, remaining);
               sessionItems.push({ word, activities });
               totalActivities += activities.length;
             }
           }
           if (sessionItems.length > 0) return sessionItems;
         } else {
           // Training is completed for today, clear cache to force new generation if user wants to restart
           localStorage.removeItem(SELECTION_CACHE_KEY);
         }
      } else {
        // Cache is invalid (wrong date, user, or version) - clear it
        // This handles the case where cache is from yesterday
        localStorage.removeItem(SELECTION_CACHE_KEY);
      }
    } catch (e) {
      // Invalid cache format - clear it
      console.warn('Invalid cache format, clearing:', e);
      localStorage.removeItem(SELECTION_CACHE_KEY);
    }
  }

  // 2. Generate New Selection
  const allWords = await fetchSavedWords(userId);
  const allExpressions = await fetchSavedExpressions(userId);
  let allItems = [...allWords, ...allExpressions];

  // Apply theme-specific examples if they exist
  allItems = allItems.map(item => {
    if (item.theme) {
      const theme = themes.find(t => t.name === item.theme);
      if (theme && theme.wordExamples && theme.wordExamples[item.word]) {
        return { ...item, exampleSentence: theme.wordExamples[item.word] };
      }
    }
    return item;
  });

  // Filter out invalid words if any (basic check)
  // Logic:
  // - Avoid repeating same words consecutively (Handled by caching today's set,
  //   checking against yesterday's set would require storing history,
  //   for now we interpret this as "randomize well" and maybe "don't pick if practiced < 12h ago"?)

  const eligibleWords = allItems.filter(w => {
    if (!w.lastPractice) return true;
    const hoursSince = (Date.now() - w.lastPractice) / (1000 * 60 * 60);
    return hoursSince > 18; // Don't pick words practiced in last 18 hours
  });

  // If we don't have enough eligible words, fall back to all items
  let pool = eligibleWords.length >= 5 ? [...eligibleWords] : [...allItems];

  // If the user still has less than 5 words in total, fetch random words from allWords
  if (pool.length < 5) {
    const numNeeded = 5 - pool.length;
    const poolWordIds = new Set(pool.map(w => w.word.toLowerCase()));
    
    // Filter available fallback words that are not already in the pool
    const availableFallbacks = fallbackWords.filter(fw => !poolWordIds.has(fw.headword.toLowerCase()));
    
    // Shuffle and pick numNeeded
    const selectedFallbacks = availableFallbacks.sort(() => Math.random() - 0.5).slice(0, numNeeded);
    
    // Map to SavedWord structure
    const newSavedWords: SavedWord[] = selectedFallbacks.map(fw => ({
      id: `unsaved_${fw.headword.toLowerCase().replace(/[^a-z0-9]/g, '_')}`,
      userId,
      word: fw.headword,
      createdAt: Date.now(),
      progress: 0,
      synonyms: [],
      successCount: 0,
      failCount: 0,
      isUnsaved: true
    }));
    
    pool = [...pool, ...newSavedWords];
  }

  // Scoring
  const scored = pool.map(w => {
    const daysSince = w.lastPractice ? (Date.now() - w.lastPractice) / (1000 * 60 * 60 * 24) : 100; // 100 days if never
    const total = w.successCount + w.failCount;
    const failRate = total === 0 ? 1 : w.failCount / total; // Prioritize unpracticed or high fail
    const progressInv = 100 - w.progress;

    // Weights
    const score =
      (daysSince * 2) +
      (failRate * 50) +
      (progressInv * 0.5) +
      (Math.random() * 20); // Randomness

    return { word: w, score };
  });

  scored.sort((a, b) => b.score - a.score);

  // Pick top words, but limit total activities to 15
  const allActivities = [
    ActivityType.Review,
    ActivityType.SentenceFormation,
    ActivityType.ListenToWrite,
    ActivityType.Translation,
    ActivityType.ImageLinking,
    ActivityType.SpeakWord,
    ActivityType.SpeakPhrase,
    ActivityType.Synonyms,
    ActivityType.ListenAndArrange,
    ActivityType.FillInTheBlanks,
    ActivityType.Matching
  ];

  const sessionItems: TrainingSessionItem[] = [];
  let totalActivities = 0;

  // Select words and activities until we reach the daily goal
  for (const scoredWord of scored) {
    if (totalActivities >= DAILY_GOAL) break;
    
    // Calculate how many activities we can add for this word
    const remaining = DAILY_GOAL - totalActivities;
    const activitiesForWord = Math.min(allActivities.length, remaining);
    
    if (activitiesForWord > 0) {
      // Get available activities for this word
      let availableActivities = [...allActivities];
      
      const isSentence = scoredWord.word.word.trim().split(/\s+/).length > 2 || scoredWord.word.type === 'expression';
      if (isSentence) {
        availableActivities = availableActivities.filter(a => a !== ActivityType.SentenceFormation && a !== ActivityType.Synonyms);
      }
      
      // Prioritize ListenAndArrange and FillInTheBlanks for words with example sentences
      // If word has exampleSentence, ensure they are more likely to be selected
      if (scoredWord.word.exampleSentence && scoredWord.word.exampleSentence.trim().split(/\s+/).length > 1) {
        // If word has a sentence, prioritize ListenAndArrange and FillInTheBlanks
        // Move them to the front of the array for better chance of selection
        availableActivities = availableActivities.filter(a => a !== ActivityType.ListenAndArrange && a !== ActivityType.FillInTheBlanks);
        availableActivities.unshift(ActivityType.ListenAndArrange, ActivityType.FillInTheBlanks);
      } else {
        // If word doesn't have a sentence, filter out FillInTheBlanks since it needs a sentence
        availableActivities = availableActivities.filter(a => a !== ActivityType.FillInTheBlanks);
      }
      
      // Shuffle activities and take the needed amount
      const shuffledActivities = [...availableActivities].sort(() => Math.random() - 0.5);
      const activities = shuffledActivities.slice(0, activitiesForWord);
      sessionItems.push({ word: scoredWord.word, activities });
      totalActivities += activities.length;
    }
  }

  // Cache
  const cacheData: CachedSelection = {
    date: today,
    userId,
    version: CACHE_VERSION,
    items: sessionItems.map(i => ({ wordId: i.word.id, activities: i.activities }))
  };
  localStorage.setItem(SELECTION_CACHE_KEY, JSON.stringify(cacheData));

  return sessionItems;
}

// --- Theme Session Logic ---

export async function getThemeSession(userId: string, themeId: string): Promise<TrainingSessionItem[]> {
  // 1. Find the theme
  const theme = themes.find(t => t.id === themeId);
  if (!theme) return [];

  // 2. Ensure all words in the theme are in savedWords
  // This is a bit heavy, but ensures we have IDs and can track progress.
  // Alternatively, we could just assume they exist or fetch/create on the fly.
  // Let's rely on fetchSavedWords to get existing ones, and map the rest.

  const allSavedWords = await fetchSavedWords(userId);
  const allSavedExpressions = await fetchSavedExpressions(userId);
  const allSavedItems = [...allSavedWords, ...allSavedExpressions];
  
  // Filter out words with invalid or missing word property
  const validSavedItems = allSavedItems.filter(w => w.word && typeof w.word === 'string');
  const savedMap = new Map(validSavedItems.map(w => [w.word.toLowerCase(), w])); // map by word string

  const sessionItems: TrainingSessionItem[] = [];

  // Activities to use
  const allActivities = [
    ActivityType.Review,
    ActivityType.SentenceFormation,
    ActivityType.ListenToWrite,
    ActivityType.Translation,
    ActivityType.ImageLinking,
    ActivityType.SpeakWord,
    ActivityType.SpeakPhrase,
    ActivityType.Synonyms,
    ActivityType.ListenAndArrange,
    ActivityType.FillInTheBlanks,
    ActivityType.Matching
  ];

  let itemsToLearn = [...theme.words, ...(theme.sentences || [])];

  // Randomize first to ensure fair chance for sentences and words when progress is equal
  for (let i = itemsToLearn.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [itemsToLearn[i], itemsToLearn[j]] = [itemsToLearn[j], itemsToLearn[i]];
  }

  // Limit to 20 exercises per theme session, prioritizing words with lower progress
  itemsToLearn.sort((a, b) => {
    const wordA = savedMap.get(a.toLowerCase());
    const wordB = savedMap.get(b.toLowerCase());
    const scoreA = wordA ? (wordA.progress || 0) : -1;
    const scoreB = wordB ? (wordB.progress || 0) : -1;
    
    // Sort by progress (lower first)
    return scoreA - scoreB;
  });

  itemsToLearn = itemsToLearn.slice(0, 20);

  for (const wordStr of itemsToLearn) {
    let savedWord = savedMap.get(wordStr.toLowerCase());

    if (!savedWord) {
      // Word not yet in user's list.
      // We need to treat it as a new word.
      const isSentence = theme.sentences?.includes(wordStr);
      const safeKey = sanitizeFirebaseKey(wordStr);
      const dbPath = isSentence ? `savedExpressions/${userId}/${safeKey}` : `savedWords/${userId}/${safeKey}`;
      const newRef = child(ref(db), dbPath);

      // Let's auto-add it with 0 progress.
      await set(newRef, {
        word: wordStr,
        theme: theme.name,
        createdAt: Date.now(),
        lastPractice: Date.now(),
        progress: 0,
        successCount: 0,
        failCount: 0,
        isAutoAdded: true
      });

      savedWord = {
        id: safeKey,
        userId,
        word: wordStr,
        theme: theme.name,
        createdAt: Date.now(),
        lastPractice: Date.now(),
        progress: 0,
        synonyms: [],
        successCount: 0,
        failCount: 0
      };
    }

    // Create activities for this word
    // Maybe fewer activities per word for a full theme session?
    // Theme usually has ~20 words. If we do 3 activities per word, that's 60 steps! Too long.
    // Maybe just 1 activity per word? Or limit to top 10 words?
    // Let's pick 1-2 activities per word.

    // Filter out Synonyms for sentences, as they don't usually have synonyms
    const isSentenceItem = theme.sentences?.includes(wordStr);
    let availableActivities = isSentenceItem 
      ? allActivities.filter(a => a !== ActivityType.Synonyms && a !== ActivityType.SentenceFormation)
      : allActivities;

    // Filter out FillInTheBlanks if the word doesn't have an example sentence
    if (!theme.wordExamples || !theme.wordExamples[wordStr]) {
      availableActivities = availableActivities.filter(a => a !== ActivityType.FillInTheBlanks);
    } else {
      // Prioritize FillInTheBlanks
      availableActivities = availableActivities.filter(a => a !== ActivityType.FillInTheBlanks);
      availableActivities.unshift(ActivityType.FillInTheBlanks);
    }

    const numActivities = 1;
    const shuffledActivities = [...availableActivities].sort(() => Math.random() - 0.5);
    const activities = shuffledActivities.slice(0, numActivities);

    // Apply word-specific example if it exists in the theme
    if (theme.wordExamples && theme.wordExamples[wordStr]) {
      savedWord = { ...savedWord, exampleSentence: theme.wordExamples[wordStr] };
    }

    sessionItems.push({
      word: savedWord,
      activities
    });
  }

  return sessionItems;
}

// Helper to get boolean array for last 7 days of training activity
// Checks localStorage for daily training completion status
export function getTrainingWeekActivity(dailyPoints: Record<string, number> = {}, userId?: string): boolean[] {
  const activity: boolean[] = [];
  // Use the same COMPLETION_KEY constant as markDailyTrainingCompleted
  const today = new Date();
  const todayKey = today.toISOString().split('T')[0];

  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateKey = d.toISOString().split('T')[0];
    const isToday = dateKey === todayKey;
    
    // Check if daily training was completed for this day
    // First check localStorage (more reliable for completion status)
    let isCompleted = false;
    if (userId) {
      // Use the same key format as markDailyTrainingCompleted
      const completionKey = `${COMPLETION_KEY}_${userId}_${dateKey}`;
      isCompleted = localStorage.getItem(completionKey) === 'true';
    }
    
    // For today: only show as completed if localStorage says so (don't use dailyPoints)
    // For past days: fallback to dailyPoints if localStorage doesn't have it
    if (!isCompleted && !isToday) {
      // For past days, check if there was significant activity (completed training usually gives many points)
      // Daily training completion typically awards many points, so check for substantial activity
      isCompleted = (dailyPoints[dateKey] || 0) >= 50; // Training completion usually gives 50+ points
    }
    
    activity.push(isCompleted);
  }

  return activity; // Chronological order [Day-6, Day-5, ..., Today]
}

// Fetch Word of the Day week activity from database
// Checks users/{userId}/wordOfTheDay/ for activity timestamps in the last 7 days
export async function fetchWordDayWeekActivity(userId: string): Promise<boolean[]> {
  const activity: boolean[] = [];
  const today = new Date();

  try {
    // Fetch user's Word of the Day exercise timestamps
    const wordDayRef = ref(db, `users/${userId}/wordOfTheDay`);
    const snapshot = await get(wordDayRef);
    
    const exerciseTimestamps: number[] = [];
    if (snapshot.exists()) {
      const data = snapshot.val();
      // Extract all timestamps from exercises (synonym, fillBlank, trueOrFalse, speak)
      Object.values(data).forEach((exercise: any) => {
        if (exercise && typeof exercise === 'object') {
          Object.values(exercise).forEach((timestamp: any) => {
            if (typeof timestamp === 'number') {
              exerciseTimestamps.push(timestamp);
            }
          });
        }
      });
    }

    // Check each of the last 7 days
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      
      const dayStart = d.getTime();
      const dayEnd = d.getTime() + (24 * 60 * 60 * 1000) - 1; // End of day
      
      // Check if any exercise timestamp falls within this day
      const hasActivity = exerciseTimestamps.some(timestamp => 
        timestamp >= dayStart && timestamp <= dayEnd
      );
      
      activity.push(hasActivity);
    }

    return activity;
  } catch (error) {
    console.error("Error fetching Word of the Day week activity:", error);
    return new Array(7).fill(false);
  }
}

export interface PastWordDay {
  date: string;
  word: string;
  timestamp: number;
}

export async function fetchPastWordsOfTheDay(pageSize: number, lastDate?: string): Promise<PastWordDay[]> {
  const historyRef = ref(db, 'wordHistory');
  let q;
  
  if (lastDate) {
    q = query(historyRef, orderByKey(), endBefore(lastDate), limitToLast(pageSize));
  } else {
    q = query(historyRef, orderByKey(), limitToLast(pageSize));
  }

  const snapshot = await get(q);
  const words: PastWordDay[] = [];
  
  if (snapshot.exists()) {
    const data = snapshot.val();
    Object.entries(data).forEach(([date, dateData]: [string, any]) => {
      if (dateData.word) {
        words.push({
          date,
          word: dateData.word,
          timestamp: dateData.timestamp
        });
      }
    });
  }

  // Firebase returns data in ascending order by key (oldest first).
  // We want newest first for pagination.
  return words.sort((a, b) => b.date.localeCompare(a.date));
}
